import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import { BarChart3, Loader2, ArrowLeft, ChevronDown } from 'lucide-react';

function StatsPage() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (!res.ok) { navigate('/login'); return; }
      const user = await res.json();
      if (!user.is_admin) { navigate('/feed'); return; }
      setAuthChecked(true);
      fetchStats(0);
    } catch {
      navigate('/login');
    }
  };

  const fetchStats = async (offset) => {
    if (offset === 0) setIsLoading(true);
    else setIsLoadingMore(true);

    try {
      const res = await fetch(`${API}/admin/page-stats?skip=${offset}&limit=10`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (offset === 0) {
          setRecords(data.records);
        } else {
          setRecords(prev => [...prev, ...data.records]);
        }
        setTotal(data.total);
        setSkip(offset + data.records.length);
      }
    } catch {}
    finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => fetchStats(skip);

  if (!authChecked || isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading stats...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 max-w-5xl mx-auto">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-btn">
        <ArrowLeft className="mr-2 h-4 w-4" /> Back
      </Button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-blue-400" /> Page Stats
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} records total - Showing {records.length}
          </p>
        </div>
      </div>

      <Card className="overflow-hidden rounded-xl" data-testid="stats-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-blue-900/60 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-white font-medium">Date</th>
                <th className="px-4 py-3 text-left text-white font-medium">Page Name</th>
                <th className="px-4 py-3 text-center text-white font-medium">Times Accessed</th>
                <th className="px-4 py-3 text-left text-white font-medium">Users</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                    No page visit records yet
                  </td>
                </tr>
              ) : (
                records.map((r, idx) => (
                  <tr
                    key={`${r.date}-${r.page}-${idx}`}
                    className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-900/50'} hover:bg-zinc-800`}
                  >
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{r.date}</td>
                    <td className="px-4 py-3 font-mono text-blue-300 max-w-[250px] truncate">{r.page}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block px-3 py-1 bg-blue-500/20 text-blue-400 font-bold rounded-full text-xs">
                        {r.access_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs max-w-[300px]">{r.users || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {records.length < total && (
          <div className="p-4 bg-zinc-900 text-center border-t border-white/10">
            <Button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              variant="outline"
              className="rounded-full"
              data-testid="load-more-stats-btn"
            >
              {isLoadingMore ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading...</>
              ) : (
                <><ChevronDown className="h-4 w-4 mr-2" /> Load More ({total - records.length} remaining)</>
              )}
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

export default StatsPage;
